/**
 * React hosts for the shared (Lit) status island (sl: badge-uniformity).
 *
 * SOLID: D — the island's look + interaction live in ONE place
 * (`@blocksuite/affine/shared/services` → `renderStatusIsland`). React-side
 * surfaces (the PDF viewer's search index and its signed-copy mark) render that
 * exact Lit template through this thin host instead of re-implementing the
 * markup/CSS, so the island can no longer drift between block types and the
 * PDF viewer.
 *
 * The shell stylesheet (`embeddingBadgeStyles`) is injected once globally; its
 * class names are not scoped, so the light-DOM island rendered here picks up
 * the identical styling that the Lit image/attachment badges get in their
 * shadow roots.
 */
import {
  embeddingBadgeStyles,
  type EmbeddingBadgeViewModel,
  renderEmbeddingBadge,
  renderStatusIsland,
  type StatusIslandViewModel,
} from '@blocksuite/affine/shared/services';
import { render, type TemplateResult } from 'lit';
import { useEffect, useMemo, useRef } from 'react';

let stylesInjected = false;

function ensureBadgeStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.setAttribute('data-embedding-badge-shell', '');
  style.textContent = embeddingBadgeStyles.cssText;
  document.head.append(style);
}

/** Renders a Lit island into a React-owned element. */
function IslandHost({
  template,
  className,
  testId,
  onOutsidePointerDown,
}: {
  template: TemplateResult | null;
  className?: string;
  testId?: string;
  onOutsidePointerDown?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureBadgeStyles();
    const host = hostRef.current;
    if (!host) return;
    render(template, host);
    return () => {
      render(null, host);
    };
  }, [template]);

  useEffect(() => {
    if (!onOutsidePointerDown || typeof document === 'undefined') return;
    const handlePointerDown = (event: PointerEvent) => {
      const host = hostRef.current;
      if (!host || event.composedPath().includes(host)) return;
      onOutsidePointerDown();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [onOutsidePointerDown]);

  if (!template) return null;
  return <div ref={hostRef} className={className} data-testid={testId} />;
}

export function EmbeddingBadge({
  vm,
  className,
  onOutsidePointerDown,
}: {
  vm: EmbeddingBadgeViewModel | null;
  className?: string;
  onOutsidePointerDown?: () => void;
}) {
  const template = useMemo(() => (vm ? renderEmbeddingBadge(vm) : null), [vm]);
  return (
    <IslandHost
      template={template}
      className={className}
      onOutsidePointerDown={onOutsidePointerDown}
    />
  );
}

export function StatusIsland({
  vm,
  className,
  testId,
  onOutsidePointerDown,
}: {
  vm: StatusIslandViewModel;
  className?: string;
  testId?: string;
  onOutsidePointerDown?: () => void;
}) {
  const template = useMemo(() => renderStatusIsland(vm), [vm]);
  return (
    <IslandHost
      template={template}
      className={className}
      testId={testId}
      onOutsidePointerDown={onOutsidePointerDown}
    />
  );
}
